import React, { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { useAccount, useDisconnect } from 'wagmi'
import { useAuth } from '../context/AuthContext'
import { useTenant } from '../context/TenantContext'
import { AuthModal } from './AuthModal'
import { LanguageSwitcher } from './LanguageSwitcher'
import { CurrencySelector } from './CurrencySelector'
import { Button } from './ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { 
  Wallet, 
  User, 
  LogOut, 
  LayoutDashboard, 
  FileCheck, 
  Settings,
  Menu,
  X
} from 'lucide-react'

export function Navbar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user, isAuthenticated, logout, isAdmin, isIssuer } = useAuth()
  const { tenant } = useTenant()
  const { isConnected } = useAccount()
  const { disconnect } = useDisconnect()
  const [authOpen, setAuthOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleLogout = () => {
    if (isConnected) {
      disconnect()
    }
    logout()
    navigate('/')
  }

  const navLinks = [
    { href: '/marketplace', label: t('nav.properties') },
    { href: '/investors', label: t('nav.forInvestors') },
    { href: '/owners', label: t('nav.forOwners') },
    { href: '/how-it-works', label: t('nav.howItWorks') },
  ]

  const getDashboardLink = () => {
    if (isAdmin) return '/admin'
    if (isIssuer) return '/issuer'
    return '/investor'
  }

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-brand-secondary ${
          scrolled 
            ? 'shadow-lg border-b border-white/10' 
            : ''
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
              <img 
                src={tenant?.branding?.logo_url || "https://uni2ecosystem.com/images/logos/tokenizelogo.png"} 
                alt={tenant?.branding?.platform_name || "2kenize"} 
                className="h-10 w-auto"
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm font-semibold tracking-wide uppercase transition-colors ${
                    location.pathname === link.href
                      ? 'text-brand-primary'
                      : 'text-white/70 hover:text-white'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right Side */}
            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <>
                  {/* Wallet Badge */}
                  {user?.wallet_address && (
                    <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 border border-brand-primary/50 bg-brand-primary/10">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                      <span className="text-xs font-mono text-white/80">
                        {user.wallet_address.slice(0, 6)}...{user.wallet_address.slice(-4)}
                      </span>
                    </div>
                  )}

                  {/* User Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        className="flex items-center gap-2 px-3 py-2 h-auto hover:bg-white/10"
                        data-testid="user-menu-btn"
                      >
                        <div className="w-10 h-10 bg-brand-primary flex items-center justify-center">
                          <User className="w-5 h-5 text-brand-secondary" />
                        </div>
                        <span className="hidden sm:block text-sm font-semibold text-white">{user?.name}</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 bg-white border-slate-200">
                      <div className="px-3 py-2 border-b border-slate-100">
                        <p className="text-sm font-semibold text-brand-secondary">{user?.name}</p>
                        <p className="text-xs text-slate-500">{user?.email || 'Wallet User'}</p>
                        <div className="mt-1 flex items-center gap-1">
                          <span className={`text-xs px-1.5 py-0.5 uppercase tracking-wider ${
                            user?.kyc_status === 'approved' 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            KYC: {user?.kyc_status}
                          </span>
                        </div>
                      </div>
                      <DropdownMenuItem 
                        onClick={() => navigate(getDashboardLink())}
                        className="text-slate-600 hover:text-brand-secondary hover:bg-brand-primary/10 cursor-pointer"
                        data-testid="dashboard-menu-item"
                      >
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        {t('userMenu.dashboard')}
                      </DropdownMenuItem>
                      {user?.kyc_status !== 'approved' && (
                        <DropdownMenuItem 
                          onClick={() => navigate('/kyc')}
                          className="text-slate-600 hover:text-brand-secondary hover:bg-brand-primary/10 cursor-pointer"
                          data-testid="kyc-menu-item"
                        >
                          <FileCheck className="w-4 h-4 mr-2" />
                          {t('userMenu.completeKyc')}
                        </DropdownMenuItem>
                      )}
                      {isAdmin && (
                        <DropdownMenuItem 
                          onClick={() => navigate('/tenant-admin')}
                          className="text-slate-600 hover:text-brand-secondary hover:bg-brand-primary/10 cursor-pointer"
                          data-testid="tenant-admin-menu-item"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Platform Config
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem 
                        className="text-slate-600 hover:text-brand-secondary hover:bg-brand-primary/10 cursor-pointer"
                        data-testid="settings-menu-item"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        {t('userMenu.settings')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-slate-100" />
                      <DropdownMenuItem 
                        onClick={handleLogout}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 cursor-pointer"
                        data-testid="logout-menu-item"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        {t('userMenu.signOut')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <Button
                  onClick={() => setAuthOpen(true)}
                  className="h-14 px-8 bg-brand-primary hover:bg-brand-primary-hover text-brand-secondary font-semibold tracking-wide rounded-none"
                  data-testid="get-started-btn"
                >
                  <Wallet className="w-4 h-4 mr-2" />
                  {t('nav.getStarted')}
                </Button>
              )}

              <CurrencySelector />
              <LanguageSwitcher />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-white/70 hover:text-white"
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden border-t border-white/10 bg-brand-secondary"
          >
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-3 py-2 text-sm font-semibold tracking-wide uppercase ${
                    location.pathname === link.href
                      ? 'text-brand-primary bg-brand-primary/10'
                      : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </motion.header>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </>
  )
}
